import 'reflect-metadata'

import { Expose, Transform, Type } from 'class-transformer'
import { CardString, StandardHandTypeWithRoyalFlush } from 'pokersolver'

import { Player } from './Player'

/**
 * A pot at a poker table and the players it's shared with
 */
export class Pot {
  amount: number

  @Expose()
  @Type(() => Player)
  players: Player[] = []

  @Expose()
  @Type(() => PotWinner)
  winners: PotWinner[] = []

  @Expose()
  @Transform(({ value }) => PotKind[value.toUpperCase() as keyof typeof PotKind])
  kind: PotKind

  constructor(amount: number, players: Player[] = [], kind: PotKind = PotKind.MAIN, winners: PotWinner[] = []) {
    this.amount = amount
    this.players = players
    this.kind = kind
    this.winners = winners
  }

  setWinners(winners: PotWinner[]) {
    this.winners = winners
  }

  getPlayerIds() {
    return this.players.reduce((playerIds, player) => {
      playerIds.push(player.getId())
      return playerIds
    }, [] as string[])
  }
}

/**
 * Helpful class to group pot winner information. Each PotWinner
 * has a specific id, name, and cards along with the amount won,
 * the hand rank, and the kind of pot won
 */
export class PotWinner {
  playerId: string
  playerName: string
  @Type(() => String)
  cards: CardString[] = []
  chipsWon: number
  @Expose()
  @Transform(({ value }) => PotKind[value.toUpperCase() as keyof typeof PotKind])
  kind: PotKind
  rankedHandName: StandardHandTypeWithRoyalFlush
  rankedHandDescription: string
  @Expose()
  @Type(() => String)
  winningCards: CardString[]
  potKind: string
  userId: string

  constructor(
    playerId: string,
    playerName: string,
    cards: CardString[],
    chipsWon: number,
    kind: PotKind,
    rankedHandName: StandardHandTypeWithRoyalFlush,
    rankedHandDescription: string,
    winningCards: CardString[],
    userId: string
  ) {
    this.playerId = playerId
    this.playerName = playerName
    this.cards = cards ?? []
    this.chipsWon = chipsWon
    this.rankedHandName = rankedHandName
    this.rankedHandDescription = rankedHandDescription
    this.winningCards = (winningCards ?? []).map((card) => {
      const value = card[0]
      const suit = card[1]
      if (value !== '1') return card
      return `A${suit}` as CardString
    })
    this.kind = kind
    this.potKind = kind
    this.userId = userId
  }
}

/**
 * The game supports two kinds of pots: MAIN and SIDE. MAIN pots are always shared
 * among all table `activePlayers` while SIDE pots are only shared with the players
 * specified in the Pot.
 */
export enum PotKind {
  MAIN = 'MAIN',
  SIDE = 'SIDE'
}
